import type { TFieldKey } from "@./suf-store";
import {
  SUF_PREFERENCE_QUESTIONS,
} from "apps/website/components/flows/fresh/sign-up/flags";
import { SHORTER_SUF_FLAG_KEY, SignupFlowSlug } from "../constants";
import type { RequiredFieldData } from "../../../shared/baseStore.types";

/**
 * Every field a page needs before the user can continue from it.
 * This gates the CTA and decides where a non-AC user resumes the flow.
 *
 * `criticalFieldsBySlug` is the subset of pages whose fields affect calories,
 * recipes or access to recipes. Only those are checked when an AC user or a
 * direct link re-enters the flow.
 */
export const requiredFieldsBySlug: Record<
  SignupFlowSlug,
  (data: RequiredFieldData) => TFieldKey[]
> = {
  [SignupFlowSlug.BASIC_INFORMATION]: ({ catId }) => [
    `cat[${catId}].name`,
    `cat[${catId}].gender`,
    `cat[${catId}].breed`,
  ],

  [SignupFlowSlug.AGE]: ({ cat, catId, inABTestGroup }) => {
    const base: TFieldKey[] = [
      `cat[${catId}].lifeStage`,
      `cat[${catId}].isNeutered`,
      `cat[${catId}].age.mode`,
    ];
    if (cat?.lifeStage === "kitten") {

      const ageMode = cat?.age?.mode ?? "months";
      if (ageMode === "weeks") {
        base.push(`cat[${catId}].age.weeks`);
      } else {
        base.push(`cat[${catId}].age.months`);
      }

    } else {
      // Birthday month is only asked for adult/senior cats, and not in the shorter-suf test variant
      const isShorterSufTest = inABTestGroup?.(SHORTER_SUF_FLAG_KEY);
      if (!isShorterSufTest) {
        base.push(`cat[${catId}].birthdayMonth`);
      }
      base.push(`cat[${catId}].age.years`);
    }

    return base;
  },

  [SignupFlowSlug.EATING_HABITS]: ({ catId }) => [
    `cat[${catId}].diet.eatingHabits`,
  ],

  [SignupFlowSlug.FLAVOURS]: ({ catId, inABTestGroup }) => {
    if (!inABTestGroup?.(SUF_PREFERENCE_QUESTIONS.key)) {
      return [];
    }
    return [ `cat[${catId}].diet.preferences.flavours` ];
  },
  [SignupFlowSlug.TEXTURES]: ({ catId, inABTestGroup }) => {
    if (!inABTestGroup?.(SUF_PREFERENCE_QUESTIONS.key)) {
      return [];
    }
    return [ `cat[${catId}].diet.preferences.textures` ];
  },

  [SignupFlowSlug.MOTIVATION]: ({ inABTestGroup }) => {
    const isShorterSufTest = inABTestGroup?.(SHORTER_SUF_FLAG_KEY);
    return isShorterSufTest ? [] : [ "persona" ];
  },

  [SignupFlowSlug.FITNESS]: ({ catId }) => [
    `cat[${catId}].body.bodyType`,
    `cat[${catId}].body.activityLevel`,
  ],

  [SignupFlowSlug.WEIGHT]: ({ cat, catId }) => {
    const base: TFieldKey[] = [
      `cat[${catId}].weight.unit`,
    ];

    if (cat?.weight?.unit === "LB") {
      base.push(`cat[${catId}].weight.valueInLb`);
    } else {
      base.push(`cat[${catId}].weight.valueInKg`);
    }

    return base;
  },

  [SignupFlowSlug.CURRENT_DIET]: ({ catId, inABTestGroup }) => {
    const isShorterSufTest = inABTestGroup?.(SHORTER_SUF_FLAG_KEY);
    if (isShorterSufTest) {
      return [];
    }
    return [
      `cat[${catId}].diet.current.type`,
      `cat[${catId}].diet.current.brand`,
    ];
  },

  [SignupFlowSlug.MEDICAL_CONDITIONS]: ({ cat, catId }) => {
    const base: TFieldKey[] = [
      `cat[${catId}].medical.hasConditions`,
      `cat[${catId}].diet.foodAllergies.hasAllergies`,
    ];
    if (cat?.medical?.hasConditions) {
      base.push(`cat[${catId}].medical.conditions`);
    }
    if (cat?.diet?.foodAllergies?.hasAllergies) {
      base.push(`cat[${catId}].diet.foodAllergies.allergens`);
    }
    return base;
  },

  [SignupFlowSlug.CAT_PARENT_DETAILS]: () => [ "email" ],

  [SignupFlowSlug.CHECKOUT]: () => [
    "firstName",
    "lastName",
    "email",
    "shippingAddress.postcode",
    "shippingAddress.address1",
    "shippingAddress.city",
    "shippingAddress.phoneNumber",
    "billingAddress.postcode",
    "billingAddress.address1",
    "billingAddress.city",
    "billingAddress.countryCode",
    "billingAddress.phoneNumber",
    "deliveryDate",
  ],

  [SignupFlowSlug.PRICING]: () => [],

  [SignupFlowSlug.RECIPES]: ({ catId }) => [
    `cat[${catId}].viewedPlan`,
  ],
  [SignupFlowSlug.WHY_FRESH]: () => [],
  [SignupFlowSlug.TREATS]: () => [],
  [SignupFlowSlug.CAT_COUNT]: () => [],
};
